import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { AreaLineChartComponent, AreaLineChartDataPoint, AreaLineChartSeries, AreaLineChartLegendItem, AreaLineChartFillBetween } from '../../../../../_shared/components/graphs/area-line-chart/area-line-chart.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { MaintenanceInsightsResponse } from '../../../models/cost-tracking-response.model';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { formatCurrencyAmount } from '../../../../../_shared/helpers/currency/currency-format.helper';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-maintenance-trend-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    AreaLineChartComponent,
    GraphTrendIconComponent,
    EmptyStateComponent,
    AccountFilterComponent,
    DateFilterComponent
  ],
  templateUrl: './maintenance-trend-modal.component.html',
  styleUrl: './maintenance-trend-modal.component.scss'
})
export class MaintenanceTrendModalComponent {
  private readonly transloco = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private costTrackingService = inject(CostTrackingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  title: string = inject(TranslocoService).translate('costTracking.charts.maintenance');
  isLoading: boolean = true;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  widgetFilter: WidgetFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  currencySymbol: string = '';
  currencyCode: string = '';

  chartData: AreaLineChartDataPoint[] = [];
  readonly chartHeight = 335;
  yTicks: number[] = [100, 80, 60, 40, 20, 0];

  // Updated series with new colors:
  // - 'You' is a line (no fill - the fill is for ROI between You and Benchmark)
  // - 'Benchmark' is a straight line (lineType: 'straight')
  // ROI is represented as the shaded area between You and Benchmark (via fillBetween)
  readonly series: AreaLineChartSeries[] = [
    { key: 'you', color: '#3873C9', label: inject(TranslocoService).translate('costTracking.trend.you'), fillOpacity: 0 },
    { key: 'benchmark', color: '#FF7F00', label: inject(TranslocoService).translate('costTracking.trend.benchmark'), fillOpacity: 0, lineType: 'straight' }
  ];

  // ROI is the shaded area between benchmark (top) and you (bottom)
  readonly roiFillBetween: AreaLineChartFillBetween = {
    topKey: 'benchmark',
    bottomKey: 'you',
    color: '#66AECF',
    fillOpacity: 0.4,
    label: inject(TranslocoService).translate('costTracking.trend.roi')
  };

  readonly legend: AreaLineChartLegendItem[] = [
    { legend: this.transloco.translate('costTracking.trend.you'), color: '#3873C9' },
    { legend: 'ROI', color: '#66AECF' },
    { legend: this.transloco.translate('costTracking.trend.benchmark'), color: '#FF7F00' }
  ];

  totalValue: number = 0;
  percentageChange: number = 5.12;

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.widgetFilter = {} as WidgetFilterData;
    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  async ngOnInit(): Promise<void> {
    this.widgetFilter = this.filterService.filter;
    await this.loadData();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.widgetFilter = {
        ...this.dateFilter,
        ...this.accountFilter
      } as WidgetFilterData;

      this.filterService.updateFilter(this.widgetFilter);

      await this.loadData();
    }
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };

      // Fetch summary, you, and benchmark data in parallel
      // ROI is not a separate data series - it's the shaded area between You and Benchmark
      const [summaryResponse, youResponse, benchmarkResponse] = await Promise.all([
        this.costTrackingService.getMaintenanceSummary(request, this.cancelSubject),
        this.costTrackingService.getMaintenanceYouInsights(request, this.cancelSubject),
        this.costTrackingService.getMaintenanceBenchmarkValue(request, this.cancelSubject)
      ]);

      const youData = youResponse.data ?? [];
      const benchmarkValue = benchmarkResponse.data ?? 0;
      const summary = summaryResponse.data;

      // Process responses and group data
      this.chartData = this.processResponses(youData, benchmarkValue);
      this.calculateYTicks(this.chartData, benchmarkValue);
      this.percentageChange = summary?.percentageChange ?? 0;
      this.totalValue = summary?.currentValue ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processResponses(
    youData: MaintenanceInsightsResponse[],
    benchmarkValue: number
  ): AreaLineChartDataPoint[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);

    const datasets = [
      { data: youData, label: 'you' }
    ];

    let groupedData: GroupedChartData[];

    // For modal: more granular data display
    // For 24 hours (1 day): show all 24 individual hours
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }

    // Add benchmark as a constant value to each data point (straight line)
    return groupedData.map(item => ({
      ...item,
      label: item['label'] || item['dateRange'],
      benchmark: benchmarkValue
    }));
  }

  private calculateYTicks(chartData: AreaLineChartDataPoint[], benchmarkValue: number): void {
    if (!chartData || chartData.length === 0) return;

    const allValues = chartData.flatMap(item => [
      Number(item['you']) || 0,
      benchmarkValue
    ]);

    const maxValue = Math.max(...allValues);
    const roundedMax = Math.ceil(maxValue / 100000) * 100000 || 100000;

    this.yTicks = [];
    for (let i = 5; i >= 0; i--) {
      this.yTicks.push(Math.round((roundedMax / 5) * i));
    }
    this.yTicks = this.yTicks.reverse();
  }

  formatYAxis = (value: number): string => {
    return formatValue(value, { currencySymbol: this.currencySymbol });
  }

  formatTooltip = (value: number): string => {
    return formatCurrencyAmount(this.currencySymbol, value);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  isUptrendPositive(): boolean {
    return true;
  }

  hasData(): boolean {
    return this.chartData.length > 0 && this.chartData.some(item => {
      return (Number(item['you']) || 0) > 0;
    });
  }

  close(): void {
    this.activeModal.close();
  }
}